const BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  })
  const data = await res.json() as { success: boolean; data?: T; error?: string; message?: string; meta?: unknown; next_refresh?: string }
  if (!data.success) {
    const err: Error & { code?: string; nextRefresh?: string } = new Error(data.message ?? data.error ?? 'API error')
    err.code = data.error
    err.nextRefresh = data.next_refresh
    throw err
  }
  // Special: horoscope endpoints return both data and meta — preserve both
  if (data.meta !== undefined) {
    return { ...(data.data as object ?? {}), meta: data.meta } as T
  }
  return data.data as T
}

export const api = {
  user: {
    upsert: (lineUid: string, displayName?: string, avatarUrl?: string) =>
      request('/api/user/upsert', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid, display_name: displayName, avatar_url: avatarUrl }),
      }),
    profile: (lineUid: string) =>
      request(`/api/user/profile?line_uid=${encodeURIComponent(lineUid)}`),
    stats: (lineUid: string) =>
      request(`/api/user/stats?line_uid=${encodeURIComponent(lineUid)}`),
    setBirthDate: (lineUid: string, birthDate: string) =>
      request('/api/user/birth-date', {
        method: 'PATCH',
        body: JSON.stringify({ line_uid: lineUid, birth_date: birthDate }),
      }),
    checkin: (lineUid: string) =>
      request<{ already_checked_in: boolean; tickets: number }>('/api/user/checkin', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid }),
      }),
  },

  dream: {
    interpret: (lineUid: string, animal: string, feeling: string, context: string) =>
      request('/api/dream', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid, animal, feeling, context }),
      }),
    history: (lineUid: string) =>
      request(`/api/dream/history?line_uid=${encodeURIComponent(lineUid)}`),
  },

  lottery: {
    latest: (type = 'government') =>
      request(`/api/lottery/latest?type=${type}`),
    history: (type = 'government', limit = 30) =>
      request(`/api/lottery/history?type=${type}&limit=${limit}`),
    heatmap: (type = 'government', years = 10) =>
      request(`/api/lottery/heatmap?type=${type}&years=${years}`),
  },

  payment: {
    init: (lineUid: string, amount: number, unlockTarget: object) =>
      request('/api/payment/init', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid, amount, unlock_target: unlockTarget }),
      }),
    uploadSlip: (refCode: string, lineUid: string, slipBase64: string) =>
      request('/api/payment/slip', {
        method: 'POST',
        body: JSON.stringify({ ref_code: refCode, line_uid: lineUid, slip_image_base64: slipBase64 }),
      }),
    status: (refCode: string) =>
      request(`/api/payment/status/${encodeURIComponent(refCode)}`),
  },

  number: {
    list: (lineUid: string) =>
      request<{ id: string; number: string; created_at: string }[]>(
        `/api/number/saved?line_uid=${encodeURIComponent(lineUid)}`
      ),
    save: (lineUid: string, number: string) =>
      request('/api/number/save', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid, number }),
      }),
    unsave: (lineUid: string, number: string) =>
      request('/api/number/unsave', {
        method: 'DELETE',
        body: JSON.stringify({ line_uid: lineUid, number }),
      }),
  },

  horoscope: {
    today: (lineUid: string) =>
      request(`/api/horoscope?line_uid=${encodeURIComponent(lineUid)}`),
    start: (lineUid: string) =>
      request('/api/horoscope/start', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid }),
      }),
    unlock: (lineUid: string, category: string) =>
      request('/api/horoscope/unlock', {
        method: 'POST',
        body: JSON.stringify({ line_uid: lineUid, category }),
      }),
  },
}
